import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton, Grid, Divider, TableCell, TableRow, Table, TableBody } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageProductTable from '../tabels/StageProductTable';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import PropTypes from 'prop-types';
import ProductList from './ProductList';
import DiffRenderer from './DiffRenderer';

function ConsumptionScheduleReview({ selectedOrg, productData, showSameFields }) {
  const [expandedRow, setExpandedRow] = useState(null); // For main schedules
  const [productDataMap, setProductDataMap] = useState({});
  const [expandedNestedItems, setExpandedNestedItems] = useState({}); // For consumption rates
  const [selectedProductId, setSelectedProductId] = useState(null);

  const scheduleColumns = [
    { name: 'schedule', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  // Helper to generate display name for consumption rates
  const getRateDisplayName = (rate) => {
    // Consumption rates are direct objects with fields property
    const fields = rate?.fields || {};
    const pricingMethod = fields.PricingMethod || fields.SBQQ__PricingMethod__c || 'Unknown';
    const lowerBound = fields.LowerBound || fields.SBQQ__LowerBound__c || '0';
    const upperBound = fields.UpperBound || fields.SBQQ__UpperBound__c || '∞';
    const price = fields.Price || fields.SBQQ__Price__c || '0';

    return `${pricingMethod}: ${lowerBound}-${upperBound} @ ${price}`;
  };

  useEffect(() => {
    if (!productData || productData.length === 0) {
      setProductDataMap({});
      return;
    }

    const generatedProductDataMap = {};

    productData.forEach((productDiff) => {
      const generatedScheduleRows = { new: [], changed: [], deleted: [], same: [] };

      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (productDiff.consumptionSchedules[type] || []).forEach((scheduleItem) => {
          // Handle wrapper key structure - unwrap the consumptionSchedules object
          const schedule = scheduleItem.consumptionSchedules || scheduleItem;
          const scheduleData = {
            id: schedule.sfId || schedule.id,
            schedule: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {schedule.name || schedule.sfId}
              </ArgonTypography>
            ),
            diffs: schedule.changes || scheduleItem.changes, // Diffs of the schedule itself
            sObjectName: 'ConsumptionSchedule', // <--- SOBJECTNAME FOR SCHEDULE
            consumptionRatesData: { new: [], changed: [], deleted: [], same: [] },
            type: type,
          };

          // Process Consumption Rates - check both wrapper and direct structure
          const ratesToProcess = scheduleItem.consumptionRates || schedule.consumptionRates;
          if (ratesToProcess) {
            ['new', 'changed', 'deleted', 'same'].forEach((rateType) => {
              (ratesToProcess[rateType] || []).forEach((rate) => {
                scheduleData.consumptionRatesData[rateType].push({
                  id: rate.sfId || rate.id || `rate-${Math.random()}`, // Ensure unique ID
                  display: getRateDisplayName(rate),
                  sObjectName: 'ConsumptionRate', // <--- SOBJECTNAME FOR RATE
                  diffs: rate.changes,
                  type: rateType,
                });
              });
            });
          }

          generatedScheduleRows[type].push(scheduleData);
        });
      });

      generatedProductDataMap[productDiff.product.sfId || productDiff.product.id || productDiff.product.external_id] = {
        scheduleRows: generatedScheduleRows,
        productName: productDiff.product.name,
      };
    });

    setProductDataMap(generatedProductDataMap);
    if (productData.length > 0 && !selectedProductId) {
      // Set initial selection only if not already set
      setSelectedProductId(
        productData[0].product.sfId || productData[0].product.id || productData[0].product.external_id
      );
    }
  }, [productData, selectedProductId]); // Add selectedProductId dependency to avoid resetting selection

  const handleRowClick = (rowId) => {
    setExpandedRow((prevExpandedRow) => (prevExpandedRow === rowId ? null : rowId));
  };

  const handleNestedItemClick = (itemId) => {
    setExpandedNestedItems((prevExpanded) => ({
      ...prevExpanded,
      [itemId]: !prevExpanded[itemId],
    }));
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
    setExpandedRow(null); // Collapse main schedule when product changes
    setExpandedNestedItems({}); // Collapse nested items when product changes
  };

  // Renders a row for a nested item (consumption rate)
  const renderNestedItemRow = (item) => {
    const isExpanded = expandedNestedItems[item.id];
    return (
      <React.Fragment key={item.id}>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
          <TableCell style={{ padding: '4px 8px', width: '100%' }} colSpan={2}>
            <ArgonBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              onClick={() => handleNestedItemClick(item.id)}
              sx={{ cursor: 'pointer', width: '100%' }}
            >
              <ArgonTypography variant="caption" color="text" fontWeight="medium" sx={{ flexGrow: 1 }}>
                {item.display}
              </ArgonTypography>
              <IconButton
                aria-label="expand nested item"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNestedItemClick(item.id);
                }}
              >
                <Icon>{isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
              </IconButton>
            </ArgonBox>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ padding: '0px', borderBottom: 'none' }} colSpan={2}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <ArgonBox
                p={2}
                bgcolor={grey[100]}
                sx={{ border: `1px solid ${grey[200]}`, borderRadius: '4px', margin: '4px 0' }}
              >
                <DiffRenderer
                  changes={item.diffs}
                  sObjectName={item.sObjectName}
                  orgType={selectedOrg.type.toLowerCase()}
                  showSameFields={showSameFields}
                />
              </ArgonBox>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  // Renders the content inside the main expanded schedule row
  const renderExpandedContent = (row) => {
    const renderNestedSection = (title, data) => {
      const hasData = ['new', 'changed', 'deleted', 'same'].some((type) => data[type].length > 0);
      if (!hasData) return null;

      return (
        <ArgonBox mt={2} mb={1}>
          <ArgonTypography variant="subtitle2" fontWeight="bold" mb={1} color="textSecondary">
            {title}
          </ArgonTypography>
          <Table size="small">
            <TableBody>
              {['new', 'changed', 'deleted', 'same'].map(
                (type) =>
                  data[type].length > 0 && (
                    <React.Fragment key={type}>
                      <TableRow>
                        <TableCell colSpan={2} style={{ padding: '4px 8px', borderBottom: 'none' }}>
                          <ArgonTypography
                            variant="caption"
                            fontWeight="medium"
                            color="text"
                            sx={{ textTransform: 'capitalize' }}
                          >
                            {type}
                          </ArgonTypography>
                        </TableCell>
                      </TableRow>
                      {data[type].map((item) => renderNestedItemRow(item))}
                    </React.Fragment>
                  )
              )}
            </TableBody>
          </Table>
        </ArgonBox>
      );
    };

    return (
      <TableRow style={{ width: '100%', padding: '0px' }}>
        <TableCell colSpan={scheduleColumns.length} style={{ padding: '0px', borderBottom: 'none' }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit style={{ width: '100%' }}>
            <ArgonBox p={3} bgcolor={grey[50]}>
              <DiffRenderer
                changes={row.diffs}
                sObjectName={row.sObjectName}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />
              <Divider sx={{ marginY: 2 }} />
              {renderNestedSection('Consumption Rates', row.consumptionRatesData)}
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderProductSection = (productName, productData) => {
    const { scheduleRows } = productData;

    const renderTableSection = (title, rows, columns, renderRowSubComponent) => {
      if (!rows || rows.length === 0) {
        return null;
      }
      return (
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                {title}
              </ArgonTypography>
              <ArgonBox
                sx={{
                  '& .MuiTableRow-root:not(:last-child)': {
                    '& td': {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {renderRowSubComponent ? (
                  <StageExpandableTable
                    rows={rows}
                    columns={columns}
                    renderRowSubComponent={({ row }) => renderRowSubComponent(row)}
                  />
                ) : (
                  <StageProductTable rows={rows} columns={columns} />
                )}
              </ArgonBox>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    };

    const formattedRows = (rows) => {
      return rows.map((row) => ({
        ...row,
        renderExpandedContent: () => renderExpandedContent(row),
        onClick: () => handleRowClick(row.id),
        selected: expandedRow === row.id,
        actions: (
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.id);
            }}
          >
            <Icon>{expandedRow === row.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        ),
      }));
    };

    const hasScheduleData = ['new', 'changed', 'deleted', 'same'].some((type) => scheduleRows[type].length > 0);

    if (!hasScheduleData) {
      return (
        <ArgonBox key={productName} mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                No consumption schedule data available for this product.
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    }

    return (
      <ArgonBox key={productName} mb={3}>
        {['new', 'changed', 'deleted', 'same'].map(
          (type) =>
            scheduleRows[type] &&
            scheduleRows[type].length > 0 &&
            renderTableSection(
              `${type.charAt(0).toUpperCase() + type.slice(1)} Consumption Schedules`,
              formattedRows(scheduleRows[type]),
              scheduleColumns,
              (row) => row.renderExpandedContent() // Pass the function to render expanded content
            )
        )}
      </ArgonBox>
    );
  };

  const productList = Object.keys(productDataMap).map((productId) => ({
    id: productId,
    name: productDataMap[productId].productName,
  }));

  return (
    <ArgonBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProductList
            products={productList}
            selectedProductId={selectedProductId}
            onProductSelect={handleProductSelect}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedProductId && productDataMap[selectedProductId] ? (
            renderProductSection(productDataMap[selectedProductId].productName, productDataMap[selectedProductId])
          ) : productList.length > 0 ? (
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                  Select a product to view its consumption schedules.
                </ArgonTypography>
              </ArgonBox>
            </Card>
          ) : (
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                  No products with consumption schedule data to display.
                </ArgonTypography>
              </ArgonBox>
            </Card>
          )}
        </Grid>
      </Grid>
    </ArgonBox>
  );
}

// Updated PropTypes
ConsumptionScheduleReview.propTypes = {
  productData: PropTypes.arrayOf(
    PropTypes.shape({
      consumptionSchedules: PropTypes.shape({
        new: PropTypes.arrayOf(PropTypes.object), // Allow null/undefined arrays
        changed: PropTypes.arrayOf(PropTypes.object),
        deleted: PropTypes.arrayOf(PropTypes.object),
        same: PropTypes.arrayOf(PropTypes.object),
      }).isRequired, // consumptionSchedules itself is required
      product: PropTypes.shape({
        name: PropTypes.string,
        sf_id: PropTypes.string,
        external_id: PropTypes.string,
        id: PropTypes.string,
      }),
    })
  ).isRequired,
  showSameFields: PropTypes.bool,
};

ConsumptionScheduleReview.defaultProps = {
  showSameFields: false, // Default value for showSameFields
};

export default ConsumptionScheduleReview;
